import type { VariantProps } from "class-variance-authority"
import React from "react"
import { cva } from "class-variance-authority"

import { BodyBase } from "@/domains/design/components/body-base"
import { BodyCaption } from "@/domains/design/components/body-caption"
import { BodySM } from "@/domains/design/components/body-sm"
import { BodyXs } from "@/domains/design/components/body-xs"
import { cn } from "@/domains/library"

const lozengeColorVariants = cva("", {
  variants: {
    color: {
      p300: "border-p300 bg-p300 text-p300",
      p400: "border-p400 bg-p400 text-p400",
      p500: "border-p500 bg-p500 text-p500",
      blue: "border-blue-300/50 bg-blue-300/25 text-blue-300",
      error: "border-error-border/[0.48] bg-error-background/[0.24] text-error-text",
      manager: "border-manager-border/[0.48] bg-manager-background/[0.24] text-manager-text",
      scalar: "border-scalar-border/[0.48] bg-scalar-background/[0.24] text-scalar-text",
      scalest: "border-scalest-border/[0.48] bg-scalest-background/[0.24] text-scalest-text",
      success: "border-success-border/[0.48] bg-success-background/[0.24] text-success-text",
      shout: "border-shout-border/[0.68] bg-shout-background/[0.58] text-shout-text",
    },
  },
  defaultVariants: {
    color: "blue",
  },
})

const lozengeSizeVariants = cva("", {
  variants: {
    size: {
      xxs: "max-h-[22px] px-[4px] py-[1px] text-[10px]",
      xs: "px-1.5 py-0.5",
      sm: "px-1.5 py-0.5",
      md: "px-2 py-1",
    },
  },
  defaultVariants: {
    size: "sm",
  },
})

function lozengeVariants({
  color,
  size,
}: {
  color?: VariantProps<typeof lozengeColorVariants>["color"]
  size?: VariantProps<typeof lozengeSizeVariants>["size"]
}) {
  return cn(
    "w-fit gap-1 whitespace-nowrap rounded-md border",
    color ? lozengeColorVariants({ color }) : "",
    size ? lozengeSizeVariants({ size }) : "",
  )
}

export interface LozengeProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "color">,
    VariantProps<typeof lozengeColorVariants>,
    VariantProps<typeof lozengeSizeVariants> {
  contentLeft?: React.ReactNode
  contentRight?: React.ReactNode
}

function Lozenge({ className, color, size, children, contentLeft, contentRight, ...props }: LozengeProps) {
  if (contentLeft && !contentRight) {
    return (
      <LeftContentLozenge color={color} size={size} contentLeft={contentLeft} className={className}>
        {children}
      </LeftContentLozenge>
    )
  }
  if (contentRight && !contentLeft) {
    return (
      <RightContentLozenge color={color} size={size} contentRight={contentRight} className={className} {...props}>
        {children}
      </RightContentLozenge>
    )
  }

  return (
    <div className={cn("flex items-center", lozengeVariants({ color, size }), className)} {...props}>
      {contentLeft}
      {renderTypographyContent(size, children)}
      {contentRight}
    </div>
  )
}

function LeftContentLozenge({ className, color, size, children, contentLeft, contentRight, ...props }: LozengeProps) {
  return (
    <div
      className={cn("flex items-center", lozengeVariants({ color }), "gap-0.5 pb-[1px] pl-1 pr-2", className)}
      {...props}
    >
      {contentLeft}
      {renderTypographyContent(size, children)}
    </div>
  )
}

function RightContentLozenge({ className, color, size, children, contentRight, ...props }: LozengeProps) {
  return (
    <div
      className={cn("flex items-center", lozengeVariants({ color }), "gap-0.75 pb-[1px] pl-2 pr-1.5", className)}
      {...props}
    >
      {renderTypographyContent(size, children)}
      {contentRight}
    </div>
  )
}

function renderTypographyContent(size: LozengeProps["size"], children: React.ReactNode) {
  if (size === "xxs") {
    return (
      <BodyCaption As="div" className="whitespace-nowrap text-t100">
        {children}
      </BodyCaption>
    )
  }
  if (size === "xs") {
    return (
      <BodyXs As="div" className="whitespace-nowrap text-t100">
        {children}
      </BodyXs>
    )
  }
  if (size === "sm") {
    return (
      <BodySM As="div" className="whitespace-nowrap text-t100">
        {children}
      </BodySM>
    )
  }
  if (size === "md") {
    return (
      <BodyBase As="div" className="whitespace-nowrap text-t100">
        {children}
      </BodyBase>
    )
  }

  return (
    <BodySM As="div" className="whitespace-nowrap text-t100">
      {children}
    </BodySM>
  )
}

export { Lozenge, lozengeVariants, lozengeColorVariants, lozengeSizeVariants }
